import React, { Fragment, useMemo, useState } from "react";
import { Container, Alert, Badge, Button, Spinner } from "reactstrap";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  Shield,
  Activity,
  Tool,
} from "react-feather";
import { Breadcrumbs } from "../../../AbstractElements";
import ValidationService from "../../../Services/validation/ValidationService";
import { APP_PATHS } from "../../../utils/appPaths";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "./validation.scss";

const STATUS_META = {
  healthy: {
    label: "Healthy",
    color: "success",
    Icon: CheckCircle,
    message: "All validation sections passed. No blocking issues detected.",
  },
  warning: {
    label: "Warnings",
    color: "warning",
    Icon: AlertTriangle,
    message: "Non-blocking warnings detected. Review recommended.",
  },
  degraded: {
    label: "Degraded",
    color: "danger",
    Icon: XCircle,
    message: "One or more validation checks failed and need attention.",
  },
  critical: {
    label: "Critical",
    color: "danger",
    Icon: XCircle,
    message: "Critical system issues detected. Immediate action required.",
  },
};

const SEVERITY_ICON = {
  critical: XCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const SEVERITY_COLOR = {
  critical: "danger",
  error: "danger",
  warning: "warning",
  info: "info",
};

const SECTION_STATUS_COLOR = {
  pass: "success",
  fail: "danger",
  warn: "warning",
};

const StatusBadge = ({ status }) => {
  const normalized = STATUS_META[status] ? status : "warning";
  const meta = STATUS_META[normalized];
  return (
    <Badge color={meta.color} className="val-badge">
      {meta.label}
    </Badge>
  );
};

const MetricPill = ({ metric }) => {
  const status = metric?.status || "pass";
  return (
    <div className={`val-metric val-metric--${status}`}>
      <p className="val-metric__value">{metric?.value ?? "—"}</p>
      <p className="val-metric__label">{metric?.label ?? ""}</p>
    </div>
  );
};

const ValidationPage = () => {
  const [deep, setDeep] = useState(false);
  const [fixingAction, setFixingAction] = useState(null);
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["validationOverview", deep],
    queryFn: () =>
      ValidationService.getOverview({
        deep,
        refresh: false,
        toleranceRevenue: "0.02",
      }),
    staleTime: deep ? 1_800_000 : 300_000,
    refetchOnWindowFocus: false,
  });

  const handleRefresh = async () => {
    try {
      const result = await queryClient.fetchQuery({
        queryKey: ["validationOverview", deep],
        queryFn: () =>
          ValidationService.getOverview({
            deep,
            refresh: true,
            toleranceRevenue: "0.02",
          }),
        staleTime: 0,
      });
      queryClient.setQueryData(["validationOverview", deep], result);
      toast.success("Server cache cleared — validation data refreshed.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to refresh validation data."
      );
    }
  };

  const statusMeta = STATUS_META[data?.status] || STATUS_META.warning;
  const StatusIcon = statusMeta.Icon;
  const summary = data?.summary ?? {};
  const issues = data?.issues ?? [];
  const sections = data?.sections ?? [];

  const sortedIssues = useMemo(() => {
    const order = { critical: 0, error: 1, warning: 2, info: 3 };
    return [...issues].sort(
      (a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9)
    );
  }, [issues]);

  const failedIncomes = useMemo(() => {
    const reconcile = sections.find((s) => s.id === "income_reconcile");
    return reconcile?.details?.failed ?? [];
  }, [sections]);

  const unreadableIncomes = useMemo(() => {
    const pipeline = sections.find((s) => s.id === "income_pipeline");
    const reconcile = sections.find((s) => s.id === "income_reconcile");
    return (
      pipeline?.details?.unreadable_clean_files ??
      reconcile?.details?.unreadable ??
      []
    );
  }, [sections]);

  const remediations = data?.remediations ?? [];

  const fixMutation = useMutation({
    mutationFn: ({ action, dryRun, incomeId }) =>
      ValidationService.remediate(action, { dryRun, incomeId }),
    onMutate: ({ action }) => setFixingAction(action),
    onSettled: () => setFixingAction(null),
    onSuccess: (result) => {
      toast.success(result.message || "Fix applied successfully.");
      queryClient.invalidateQueries({ queryKey: ["validationOverview"] });
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Fix failed.";
      toast.error(msg);
    },
  });

  const runFix = (action, dryRun = false, incomeId = null) => {
    fixMutation.mutate({ action, dryRun, incomeId });
  };

  const applicableFixes = useMemo(() => {
    const fromIssues = sortedIssues
      .filter((i) => i.fix?.action)
      .map((i) => i.fix.action);
    return [...new Set(fromIssues)];
  }, [sortedIssues]);

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Validation" />
      <Container fluid className="rd-validation">
        <div className="val-toolbar">
          <div>
            <p className="val-deep-note mb-0">
              Monitor royalties integrity, income pipeline health, catalog orphans, and system checks.
            </p>
          </div>
          <div className="val-toolbar__actions">
            <Button
              color={deep ? "primary" : "light"}
              size="sm"
              outline={!deep}
              onClick={() => setDeep((v) => !v)}
              disabled={isFetching}
            >
              <Activity size={14} className="me-1" />
              {deep ? "Deep check on" : "Deep check off"}
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={handleRefresh}
              disabled={isFetching}
              title="Clear server cache and run all checks again"
            >
              {isFetching ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <RefreshCw size={14} className="me-1" />
              )}
              {data?.cached ? "Refresh cache" : "Refresh"}
            </Button>
          </div>
        </div>

        {deep && (
          <Alert color="info" className="py-2 px-3 mb-3">
            Deep check runs golden fixtures, FUGA benchmarks, and catalog orphans. S3 income reconcile and full catalog audit run when you click <strong>Refresh cache</strong> (may take several minutes).
          </Alert>
        )}

        {data?.cached && !isFetching && (
          <Alert color="light" className="py-2 px-3 mb-3 border">
            Showing cached results (up to {deep ? "30" : "5"} minutes old). Click <strong>Refresh cache</strong> to clear the server cache and run checks again.
          </Alert>
        )}

        {isError && (
          <Alert color="danger">
            Failed to load validation data: {error?.response?.data?.message || error?.message || "Unknown error"}
          </Alert>
        )}

        {isLoading && !data ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Running validation checks…</p>
          </div>
        ) : data ? (
          <>
            <div className="val-status-banner">
              <div className="val-status-banner__icon">
                <StatusIcon
                  size={28}
                  className={`text-${statusMeta.color}`}
                  aria-hidden
                />
              </div>
              <div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h2 className="val-status-banner__title">System validation</h2>
                  <StatusBadge status={data.status} />
                  {data.deep && <Badge color="secondary">Deep</Badge>}
                </div>
                <p className="val-status-banner__meta">
                  {statusMeta.message}
                  {data.timestamp && (
                    <> · Last run {new Date(data.timestamp).toLocaleString()}</>
                  )}
                  {typeof data.duration_ms === "number" && data.duration_ms > 0 && (
                    <> · {data.duration_ms < 1000
                      ? `${data.duration_ms}ms`
                      : `${(data.duration_ms / 1000).toFixed(1)}s`}</>
                  )}
                  {data.cached && <> · cached</>}
                </p>
              </div>
            </div>

            <div className="val-summary-grid">
              <div className="val-summary-card">
                <p className="val-summary-card__value text-success">
                  {summary.passed_sections ?? 0}/{summary.total_sections ?? 0}
                </p>
                <p className="val-summary-card__label">Sections passed</p>
              </div>
              <div className="val-summary-card">
                <p className="val-summary-card__value text-danger">
                  {summary.failed_sections ?? 0}
                </p>
                <p className="val-summary-card__label">Failed sections</p>
              </div>
              <div className="val-summary-card">
                <p className="val-summary-card__value text-warning">
                  {(summary.warning_issues ?? 0) + (summary.warning_sections ?? 0)}
                </p>
                <p className="val-summary-card__label">Warnings</p>
              </div>
              <div className="val-summary-card">
                <p className="val-summary-card__value">
                  {summary.total_issues ?? 0}
                </p>
                <p className="val-summary-card__label">Total issues</p>
              </div>
            </div>

            {applicableFixes.length > 0 && (
              <article className="val-panel val-panel--full mb-3">
                <header className="val-panel__header">
                  <h3 className="val-panel__title">
                    <Tool size={14} className="me-1" />
                    Automated fixes
                  </h3>
                </header>
                <div className="val-panel__body p-3">
                  <p className="val-deep-note mb-3">
                    One-click repairs based on detected issues. Safe fixes update catalog links or clear stale paths — they do not delete distribution revenue.
                  </p>
                  <div className="val-fix-grid">
                    {remediations
                      .filter((r) => applicableFixes.includes(r.action))
                      .map((rem) => (
                        <div className="val-fix-card" key={rem.action}>
                          <h4 className="val-fix-card__title">{rem.label}</h4>
                          <p className="val-fix-card__desc">{rem.description}</p>
                          <div className="val-fix-card__actions">
                            <Button
                              color="primary"
                              size="sm"
                              disabled={fixMutation.isPending}
                              onClick={() => runFix(rem.action, false)}
                            >
                              {fixingAction === rem.action ? (
                                <Spinner size="sm" className="me-1" />
                              ) : null}
                              Fix now
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </article>
            )}

            <div className="val-layout">
              <article className="val-panel">
                <header className="val-panel__header">
                  <h3 className="val-panel__title">
                    <Shield size={14} className="me-1" />
                    Issues requiring attention
                  </h3>
                  <Badge color="light">{sortedIssues.length}</Badge>
                </header>
                <div className="val-panel__body">
                  {sortedIssues.length === 0 ? (
                    <p className="val-empty">No issues detected — all checks passed.</p>
                  ) : (
                    sortedIssues.map((issue, index) => {
                      const Icon = SEVERITY_ICON[issue.severity] || Info;
                      return (
                        <div className="val-issue" key={`${issue.title}-${index}`}>
                          <div className="val-issue__head">
                            <Icon
                              size={18}
                              className={`text-${SEVERITY_COLOR[issue.severity] || "secondary"} mt-1`}
                            />
                            <div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <h4 className="val-issue__title">{issue.title}</h4>
                                <Badge color={SEVERITY_COLOR[issue.severity] || "secondary"} pill>
                                  {issue.severity}
                                </Badge>
                                {issue.category && (
                                  <Badge color="light">{issue.category}</Badge>
                                )}
                              </div>
                              <p className="val-issue__message">{issue.message}</p>
                              {issue.action && (
                                <p className="val-issue__action">
                                  <strong>Action:</strong> {issue.action}
                                </p>
                              )}
                              {issue.fix?.action && (
                                <div className="val-issue__fix mt-2">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    outline
                                    disabled={fixMutation.isPending}
                                    onClick={() => runFix(issue.fix.action, false)}
                                  >
                                    {fixingAction === issue.fix.action ? (
                                      <Spinner size="sm" className="me-1" />
                                    ) : null}
                                    {issue.fix.label || "Fix"}
                                  </Button>
                                </div>
                              )}
                              {issue.meta?.income_ids?.length > 0 && (
                                <ul className="val-link-list">
                                  {issue.meta.income_ids.map((id) => (
                                    <li key={id}>
                                      <Link to={APP_PATHS.incomeReport(id)}>Income #{id}</Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </article>

              <article className="val-panel">
                <header className="val-panel__header">
                  <h3 className="val-panel__title">Validation sections</h3>
                </header>
                <div className="val-panel__body">
                  {sections.map((section) => (
                    <div className="val-section" key={section.id}>
                      <div className="val-section__head">
                        <div>
                          <h4 className="val-section__title">{section.title}</h4>
                          <p className="val-section__desc">{section.description}</p>
                        </div>
                        <Badge color={SECTION_STATUS_COLOR[section.status] || "secondary"}>
                          {section.status}
                        </Badge>
                      </div>
                      <div className="val-metrics">
                        {(section.metrics ?? []).map((metric) => (
                          <MetricPill key={`${section.id}-${metric.label}`} metric={metric} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </article>

              {unreadableIncomes.length > 0 && (
                <article className="val-panel val-panel--full">
                  <header className="val-panel__header">
                    <h3 className="val-panel__title">Unreadable clean CSV files</h3>
                    <Badge color="warning">{unreadableIncomes.length}</Badge>
                  </header>
                  <div className="val-panel__body p-3">
                    <p className="val-deep-note mb-3">
                      These incomes reference clean files that are missing or inaccessible on S3 (e.g. income #27).
                    </p>
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Income</th>
                            <th>Clean file path</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unreadableIncomes.map((row) => (
                            <tr key={row.income_id}>
                              <td>
                                <Link to={APP_PATHS.incomeReport(row.income_id)}>
                                  #{row.income_id}
                                </Link>
                              </td>
                              <td className="text-muted small">{row.clean_file ?? "—"}</td>
                              <td className="small">
                                {row.message ?? "Missing on S3"}
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0 ms-2"
                                  disabled={fixMutation.isPending}
                                  onClick={() =>
                                    runFix("clear_stale_clean_files", false, row.income_id)
                                  }
                                >
                                  Clear path
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </article>
              )}

              {failedIncomes.length > 0 && (
                <article className="val-panel val-panel--full">
                  <header className="val-panel__header">
                    <h3 className="val-panel__title">Failed income reconciles</h3>
                    <Badge color="danger">{failedIncomes.length}</Badge>
                  </header>
                  <div className="val-panel__body p-3">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Income</th>
                            <th>Clean CSV</th>
                            <th>Database</th>
                            <th>Diff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {failedIncomes.map((row) => (
                            <tr key={row.income_id}>
                              <td>
                                <Link to={APP_PATHS.incomeReport(row.income_id)}>
                                  #{row.income_id}
                                </Link>
                              </td>
                              <td>{row.clean_title_revenue ?? "—"}</td>
                              <td>{row.db_title_revenue ?? "—"}</td>
                              <td className="text-danger">
                                {row.revenue_diff ?? "—"}
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0 ms-2"
                                  disabled={fixMutation.isPending}
                                  onClick={() =>
                                    runFix("backfill_distribution_revenue", false, row.income_id)
                                  }
                                >
                                  Backfill
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </article>
              )}
            </div>
          </>
        ) : null}
      </Container>
    </Fragment>
  );
};

export default ValidationPage;
